// src/components/roomType/RoomTypeRatePlan.js
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Link,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Grid,
  Skeleton,
  Divider,
} from "@mui/material";

import DialogSeeMoreRoomType from "./DialogSeeMoreRoomType";
import RatePlanDetail from "./DialogDetailRatePlan";
import {
  obtenerMuestroTiposHabitacion,
  obtenerServiciosDeHabitacion,
  obtenerMuestroCarrito,
  rutTieneReserva,
  obtenerDatosHotel,
  grabarCarrito,
  obtenerRegimenesTipoHab,
  obtenerRegimenPorTipoCliente,
  eliminarItemCarrito,
  tarifaNoLineal,
  obtenerImagenCabezal,
  datosPax,
} from "../../services/motorService";
import { roomServices } from "../roomServices/RoomServices";
import RoomTypeRatePlanContext from "../contexts/RoomTypeRatePlanContext";
import useSession from "../../services/session";
import RoomsContext from "../contexts/RoomsContext";
import Cookies from "js-cookie";
import LinearProgress from "@mui/material/LinearProgress";
import Collapse from "@mui/material/Collapse";
import { useLocation, useNavigate } from "react-router-dom";

// ===== Helpers ===============================================================
async function withRetry(task, { retries = 2, delayMs = 400 } = {}) {
  let lastErr;
  for (let i = 0; i <= retries; i++) {
    try {
      return await task();
    } catch (err) {
      lastErr = err;
      const code = err?.code || err?.response?.status;
      const transient =
        code === "ERR_NETWORK" || code === "ECONNRESET" || code === 429;
      if (!transient || i === retries) break;
      await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
    }
  }
  throw lastErr;
}

// Normaliza respuestas que a veces vienen como objeto (no array)
const normalizeToArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

async function runInBatches(items, size, worker) {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    const batch = items.slice(i, i + size);
    const settled = await Promise.allSettled(batch.map(worker));
    out.push(...settled);
  }
  return out;
}

// ✅ Pool de concurrencia real: a diferencia de runInBatches (que espera a que
// TERMINEN los 3 de un lote antes de arrancar el próximo, desperdiciando los
// "lugares" que ya quedaron libres si uno tarda más que los otros), acá apenas
// se libera un lugar se toma el siguiente tipo de habitación de la cola. Mismo
// tipo de resultado que Promise.allSettled ({status, value|reason} en el mismo
// orden que "items"), para no tener que tocar el código que lo consume.
async function runWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;

  const runner = async () => {
    while (true) {
      const current = cursor++;
      if (current >= items.length) return;
      try {
        const value = await worker(items[current]);
        results[current] = { status: "fulfilled", value };
      } catch (reason) {
        results[current] = { status: "rejected", reason };
      }
    }
  };

  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    runner
  );
  await Promise.all(workers);
  return results;
}

// ✅ Cache de "servicios de habitación" por (hotel, tipo de habitación).
// Esta llamada NO depende de las fechas buscadas, así que no hace falta
// volver a pedirla cada vez que el usuario busca (o modifica la búsqueda y
// vuelve a buscar): la primera vez que se pide un tipo de habitación queda
// en memoria para el resto de la sesión de la pestaña.
const serviciosPorTipoHabCache = new Map();

function getServiciosDeHabitacionCacheados(hotCod, tipoHabCodigo) {
  const key = `${hotCod}:${tipoHabCodigo}`;

  if (!serviciosPorTipoHabCache.has(key)) {
    const promise = withRetry(() =>
      obtenerServiciosDeHabitacion(hotCod, tipoHabCodigo)
    ).catch((err) => {
      // si falla, no dejamos la promesa rota en cache: la próxima vez reintenta
      serviciosPorTipoHabCache.delete(key);
      throw err;
    });
    serviciosPorTipoHabCache.set(key, promise);
  }

  return serviciosPorTipoHabCache.get(key);
}

// ✅ Cache (con vencimiento corto) de "tipos de habitación" por hotel+fechas.
// En los logs se ve que fetchRoomTypes se ejecuta varias veces seguidas para
// LA MISMA búsqueda (porque cambian "rooms"/"cartItems" en cadena apenas se
// monta el componente), y cada ejecución repetía la llamada más pesada de
// todas (obtenerMuestroTiposHabitacion, ~3s en los logs que pasaste) aunque
// el hotel y las fechas fueran exactamente los mismos. Con este TTL corto,
// esas repeticiones "en cadena" de la misma búsqueda reutilizan la respuesta
// en vez de volver a pegarle al backend; pasado el TTL, un "Buscar" nuevo
// (o la misma búsqueda un rato después) sí vuelve a pedir cupos actualizados.
const TIPOS_HABITACION_CACHE_TTL_MS = 15000;
const tiposHabitacionCache = new Map(); // key -> { ts, promise }

function getTiposHabitacionCacheados(hotCod, desde, hasta) {
  const key = `${hotCod}:${desde}:${hasta}`;
  const cached = tiposHabitacionCache.get(key);
  const now = Date.now();

  if (cached && now - cached.ts < TIPOS_HABITACION_CACHE_TTL_MS) {
    return cached.promise;
  }

  const promise = withRetry(() =>
    obtenerMuestroTiposHabitacion(hotCod, desde, hasta)
  ).catch((err) => {
    tiposHabitacionCache.delete(key);
    throw err;
  });

  tiposHabitacionCache.set(key, { ts: now, promise });
  return promise;
}

const getGroupId = (item) => {
  const h = item?.habitacioncarrito;
  if (typeof h === "number" && h > 0) return h; // hija -> id base
  return item?.CarritoItemLinea; // base o sin vínculo
};

const sameStay = (a, b) =>
  a?.CarritoItemDesde === b?.CarritoItemDesde &&
  a?.CarritoItemHasta === b?.CarritoItemHasta;

const isPaxNoLineal = (rp) =>
  String(rp?.TarifaAplica || "").toUpperCase() === "N";

const n = (value) => {
  if (value == null) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  let s = String(value).trim();
  if (!s) return 0;

  if (s.includes(",") && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
    const v = parseFloat(s);
    return Number.isFinite(v) ? v : 0;
  }

  if (s.includes(",") && !s.includes(".")) {
    s = s.replace(",", ".");
    const v = parseFloat(s);
    return Number.isFinite(v) ? v : 0;
  }

  const v = parseFloat(s);
  return Number.isFinite(v) ? v : 0;
};

const calcNoLinealTotal = ({ adults, children, nl }) => {
  const tarifa1 = n(nl?.tarifa1);
  const tarifa2 = n(nl?.tarifa2);
  const tarifa3 = n(nl?.tarifa3);
  const tarifa4 = n(nl?.tarifa4);
  const tarifa5 = n(nl?.tarifa5);
  const menor1 = n(nl?.menor1);
  const menor2 = n(nl?.menor2);
  const adicional = n(nl?.tarifapaxadicional);

  let totalAdult = 0;
  if (adults <= 0) totalAdult = 0;
  else if (adults === 1) totalAdult = tarifa1;
  else if (adults === 2) totalAdult = tarifa2;
  else if (adults === 3) totalAdult = tarifa3;
  else if (adults === 4) totalAdult = tarifa4;
  else if (adults === 5) totalAdult = tarifa5;
  else totalAdult = tarifa5 + (adults - 5) * adicional;

  let totalChild = 0;
  if (children <= 0) totalChild = 0;
  else if (children === 1) totalChild = menor1;
  else if (children === 2) totalChild = menor2;
  else totalChild = children * menor1;

  return totalAdult + totalChild;
};

const addDays = (yyyyMMdd, days) => {
  const [y, m, d] = yyyyMMdd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

// Genera tramos INCLUSIVOS [from,to] de maxDays (ej 30)
const splitIntoMaxDayRanges = (from, to, maxDays = 30) => {
  const ranges = [];
  let curFrom = from;

  while (curFrom <= to) {
    let curTo = addDays(curFrom, maxDays - 1);
    if (curTo > to) curTo = to;
    ranges.push({ from: curFrom, to: curTo });
    curFrom = addDays(curTo, 1);
  }
  return ranges;
};

// ✅ helper: date -> YYYY-MM-DD usando FECHA LOCAL
const pad2 = (v) => String(v).padStart(2, "0");

const toYmd = (value) => {
  if (!value) return "";

  // dayjs / moment-like
  if (value?.format && typeof value.format === "function") {
    return value.format("YYYY-MM-DD");
  }

  // string ya en formato YYYY-MM-DD
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(
      parsed.getDate()
    )}`;
  }

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// ============================================================================

function RoomTypeRatePlan({ rutData, searchTrigger }) {
  const { selectedDetails, cartItems, setCartItems } = useContext(
    RoomTypeRatePlanContext
  );

  const {
    rooms,
    updateRoom,
    bindGroupToSlot,
    setSlotPax,
    getSlotPax,
    groupSlotMap,
    unbindGroup,
    paxChangeSignal,
  } = useContext(RoomsContext);

  const [roomTypeList, setRoomTypeList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [ratePlanDialogOpen, setRatePlanDialogOpen] = useState(false);
  const [selectedRatePlan, setSelectedRatePlan] = useState(null);

  const [isSelecting, setIsSelecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHydratingList, setIsHydratingList] = useState(false);
  const fetchIdRef = useRef(0);
  const abortRef = useRef(null);

  const [datosHotel, setDatosHotel] = useState({});
  const [tipoTarifaABuscar, setTipoTarifaABuscar] = useState(null);
  const [cliente, setCliente] = useState(null);

  const [listVersion, setListVersion] = useState(0);
  const stablePaxBySlotRef = useRef({});

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const hotcodParam = params.get("hotcod");
  const hotCod = hotcodParam ?? Cookies.get("hotCod") ?? null;
  const sesion = useSession();

  const H27_SEASON_FROM = "2026-01-15";
  const H27_SEASON_TO = "2026-03-15";

  const [h27BlockedSeason, setH27BlockedSeason] = useState(false);

  const BANCO_ESTADO_HOTELS = ["50", "51", "52", "53", "54", "55", "56", "57", "58", "59", "60"];

  const [bancoEstadoRut, setBancoEstadoRut] = useState("");
  const [bancoEstadoTipoCliente, setBancoEstadoTipoCliente] = useState(null);
  const [bancoEstadoLoading, setBancoEstadoLoading] = useState(false);


  const getDocumentoH27 = () => {
    const doc0 = Array.isArray(rutData?.DatosDocumento)
      ? rutData.DatosDocumento[0]
      : null;
    return String(doc0?.PaxDocumento || "").trim();
  };

  // =======================
  // ✅ Alternativas (cards)
  // =======================
  const HERO_GROUP = ["6", "7", "8", "9", "10"];

  const [altLoading, setAltLoading] = useState(false);
  const [altHotels, setAltHotels] = useState([]);
  const [altFinished, setAltFinished] = useState(false);

  // =======================
  // ✅ Períodos cercanos (TODOS los hoteles)
  // =======================
  const [nearLoading, setNearLoading] = useState(false);
  const [nearFinished, setNearFinished] = useState(false);
  const [nearHasMore, setNearHasMore] = useState(false);
  const [nearPeriods, setNearPeriods] = useState([]);

  const nearScrollRef = useRef(null);

  const nearOffsetsRef = useRef([]);
  const nearCursorRef = useRef(0);
  const nearSearchTokenRef = useRef(0);

  const fmtRange = (desde, hasta) => {
    try {
      const f = new Date(`${desde}T00:00:00Z`);
      const t = new Date(`${hasta}T00:00:00Z`);
      const df = new Intl.DateTimeFormat("es-UY", {
        day: "2-digit",
        month: "short",
        timeZone: "UTC",
      });
      return `${df.format(f)} - ${df.format(t)}`;
    } catch {
      return `${desde} - ${hasta}`;
    }
  };

  const diffNights = (desde, hasta) => {
    const a = new Date(`${desde}T00:00:00Z`);
    const b = new Date(`${hasta}T00:00:00Z`);
    const ms = b - a;
    return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
  };

  const redirectToSameHotelWithDates = (desde, hasta) => {
    const qs = new URLSearchParams(location.search);

    qs.set("hotcod", String(hotCod));
    qs.set("desde", desde);
    qs.set("hasta", hasta);

    navigate(
      { pathname: location.pathname, search: `?${qs.toString()}` },
      { replace: false }
    );
  };

  const NEAR_PAGE_SIZE = 4;
  const NEAR_BATCH_SIZE = 4;
  const NEAR_MAX_DAYS = 14;

  const resetNearSearch = () => {
    setNearPeriods([]);
    setNearHasMore(false);
    setNearFinished(false);
    setNearLoading(false);
    nearOffsetsRef.current = [];
    nearCursorRef.current = 0;
    nearSearchTokenRef.current += 1;
  };

  const fetchMoreNearPeriods = async ({ desde, hasta, initial = false } = {}) => {
    if (!desde || !hasta) return;

    if (nearLoading) return;

    const token = nearSearchTokenRef.current;
    setNearLoading(true);

    try {
      if (initial || nearOffsetsRef.current.length === 0) {
        const offsets = [];
        for (let i = 1; i <= NEAR_MAX_DAYS; i++) offsets.push(-i, +i);
        nearOffsetsRef.current = offsets;
        nearCursorRef.current = 0;
      }

      const collected = [];

      const worker = async (offsetDays) => {
        const d2 = addDays(desde, offsetDays);
        const h2 = addDays(hasta, offsetDays);

        const tipos = await withRetry(() =>
          obtenerMuestroTiposHabitacion(hotCod, d2, h2)
        );

        const disponibles = Array.isArray(tipos)
          ? tipos.filter((t) => Number(t?.TipoHabQuedan || 0) > 0)
          : [];

        if (disponibles.length === 0) return null;

        const fromQty = disponibles.reduce(
          (acc, t) => acc + Number(t?.TipoHabQuedan || 0),
          0
        );
        return { desde: d2, hasta: h2, offsetDays, fromQty };
      };

      while (
        collected.length < NEAR_PAGE_SIZE &&
        nearCursorRef.current < nearOffsetsRef.current.length
      ) {
        const start = nearCursorRef.current;
        const end = Math.min(start + NEAR_BATCH_SIZE, nearOffsetsRef.current.length);
        const batchOffsets = nearOffsetsRef.current.slice(start, end);
        nearCursorRef.current = end;

        const settled = await runInBatches(batchOffsets, NEAR_BATCH_SIZE, worker);

        if (nearSearchTokenRef.current !== token) return;

        const foundBatch = settled
          .filter((s) => s.status === "fulfilled" && s.value)
          .map((s) => s.value);

        for (const item of foundBatch) {
          collected.push(item);
          if (collected.length >= NEAR_PAGE_SIZE) break;
        }
      }

      setNearPeriods((prev) => {
        const key = (x) => `${x.desde}|${x.hasta}`;
        const set = new Set(prev.map(key));
        const merged = [...prev];
        for (const it of collected) {
          if (!set.has(key(it))) merged.push(it);
        }
        return merged;
      });

      const hasMore = nearCursorRef.current < nearOffsetsRef.current.length;
      setNearHasMore(hasMore);
      setNearFinished(true);
    } catch (e) {
      console.error("[NEAR_PERIODS] error", e);
      setNearHasMore(false);
      setNearFinished(true);
    } finally {
      if (nearSearchTokenRef.current === token) {
        setNearLoading(false);
      }
    }
  };

  const handleNearScroll = () => {
    const el = nearScrollRef.current;
    if (!el) return;
    if (nearLoading || !nearHasMore) return;

    const thresholdPx = 80;
    const remaining = el.scrollWidth - el.scrollLeft - el.clientWidth;
    if (remaining < thresholdPx) {
      const desde = toYmd(selectedDetails?.startDate);
      const hasta = toYmd(selectedDetails?.endDate);
      fetchMoreNearPeriods({ desde, hasta, initial: false });
    }
  };

  // =======================
  // ✅ Alternativos
  // =======================
  const hotelMetaCacheRef = useRef({});
  const bannerCacheRef = useRef({});

  const getBaseUrl = () => {
    const p = location.pathname || "";
    const idx = p.indexOf("/disponibilidad");
    if (idx > 0) return p.substring(0, idx);
    return "/MotorReservasAdmintour";
  };

  const redirectToHotel = (hotelCode, hotelName = "") => {
    const base = getBaseUrl();

    const qs = new URLSearchParams({
      hotcod: String(hotelCode),
      showRedirectOverlay: "1",
      hotelName: hotelName || "",
    });

    window.location.href = `${base}/disponibilidad/habitaciones?${qs.toString()}`;
  };

  const getHotelBanner = async (hotelCode) => {
    const key = String(hotelCode);
    if (key in bannerCacheRef.current) return bannerCacheRef.current[key];

    try {
      const imagenBase64 = await withRetry(() => obtenerImagenCabezal(key));

      if (
        typeof imagenBase64 === "string" &&
        imagenBase64.startsWith("Imagen del Cabezal Sin Datos para el hotel")
      ) {
        bannerCacheRef.current[key] = null;
        return null;
      }

      const url = `data:image/png;base64,${imagenBase64}`;
      bannerCacheRef.current[key] = url;
      return url;
    } catch (e) {
      console.error("[ALT_HOTELS][BANNER] error", e);
      bannerCacheRef.current[key] = null;
      return null;
    }
  };

  const buildHotelMeta = (hotel, hotcod) => {
    return {
      hotcod: String(hotcod),
      name: hotel?.MotorHotNombre || `Hotel ${hotcod}`,
      country: hotel?.MotorHotPais || "",
      phone: hotel?.MotorHotTelefono || "",
      web: hotel?.MotorHotWeb || "",
    };
  };

  const getHotelMeta = async (hotelCode) => {
    const key = String(hotelCode);
    if (hotelMetaCacheRef.current[key]) return hotelMetaCacheRef.current[key];

    const data = await withRetry(() => obtenerDatosHotel(key));
    const meta = buildHotelMeta(data, key);

    hotelMetaCacheRef.current[key] = meta;
    return meta;
  };

  const findAlternativeHotels = async ({ desde, hasta }) => {
    const current = String(hotCod);
    if (!HERO_GROUP.includes(current)) {
      setAltHotels([]);
      setAltFinished(true);
      return;
    }

    const candidates = HERO_GROUP.filter((h) => h !== current);

    setAltFinished(false);
    setAltLoading(true);
    try {
      const worker = async (h) => {
        const tipos = await withRetry(() =>
          obtenerMuestroTiposHabitacion(h, desde, hasta)
        );

        const disponibles = Array.isArray(tipos)
          ? tipos.filter((t) => Number(t?.TipoHabQuedan || 0) > 0)
          : [];

        if (disponibles.length === 0) return null;

        const [meta, banner] = await Promise.all([getHotelMeta(h), getHotelBanner(h)]);

        const roomTypes = disponibles
          .map((t) => t?.TipoHabNombre)
          .filter(Boolean)
          .slice(0, 4);

        const fromQty = disponibles.reduce(
          (acc, t) => acc + Number(t?.TipoHabQuedan || 0),
          0
        );

        return {
          ...meta,
          banner,
          roomTypes,
          fromQty,
        };
      };

      const settled = await runInBatches(candidates, 3, worker);

      const available = settled
        .filter((s) => s.status === "fulfilled" && s.value)
        .map((s) => s.value);

      setAltHotels(available);
      setAltFinished(true);
    } catch (e) {
      console.error("[ALT_HOTELS] error", e);
      setAltHotels([]);
      setAltFinished(true);
    } finally {
      setAltLoading(false);
    }
  };

  // ===== init =====
  useEffect(() => {
    const init = async () => {
      try {
        const data = await obtenerDatosHotel(hotCod);
        console.log("Datos del hotel", data);
        await obtenerMuestroCarrito(hotCod, sesion);
        setDatosHotel(data);
      } catch (e) {
        console.error(e);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //BANCO ESTADO 
  // ===== Banco Estado: obtiene RUT desde JWE y tipo de cliente =====
  useEffect(() => {
    const obtenerClienteBancoEstado = async () => {
      if (!BANCO_ESTADO_HOTELS.includes(String(hotCod))) return;

      const jwe = params.get("t");

      if (!jwe) {
        console.warn("[BANCO ESTADO] No se recibió parámetro t");
        return;
      }

      try {
        setBancoEstadoLoading(true);

        const response = await fetch(
          "https://gx18v2.admintour.com/jwe_decrypt/jwe/decrypt",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              jwe,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("No se pudo desencriptar el JWE.");
        }
        const hotel = await obtenerDatosHotel(hotCod);

        const data = await response.json();
        const rut = data?.rut || "";

        setBancoEstadoRut(rut);

        if (!rut) {
          throw new Error("El JWE no devolvió RUT.");
        }
        console.log("DATOS DEL HOTEEEEEL", hotel);
        const datosCliente = await datosPax(hotel.MotorHotAdmintour, rut);
        console.log("datos cliente", datosCliente);
        const tipoCliente =
          datosCliente?.DatosDocumento?.[0]?.PaxTipCliDesc || null;

        setBancoEstadoTipoCliente(tipoCliente);

        console.log("[BANCO ESTADO] RUT:", rut);
        console.log("[BANCO ESTADO] Datos cliente:", datosCliente);
        console.log("[BANCO ESTADO] Tipo cliente:", tipoCliente);
      } catch (error) {
        console.error("[BANCO ESTADO] Error obteniendo cliente", error);
        setBancoEstadoRut("");
        setBancoEstadoTipoCliente(null);
      } finally {
        setBancoEstadoLoading(false);
      }
    };

    obtenerClienteBancoEstado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotCod]);

  const paxSignature = React.useMemo(() => {
    try {
      return (rooms || [])
        .map((r) => {
          const rn = r?.roomNumber;
          const p = typeof getSlotPax === "function" ? getSlotPax(rn) : null;
          const a = p?.adults ?? r?.adultsCount ?? 0;
          const c = p?.children ?? r?.childrenCount ?? 0;
          return `${rn}:${a}-${c}`;
        })
        .join("|");
    } catch {
      return (rooms || [])
        .map(
          (r) =>
            `${r?.roomNumber}:${r?.adultsCount ?? 0}-${r?.childrenCount ?? 0}`
        )
        .join("|");
    }
  }, [rooms, getSlotPax]);

  const getStablePaxForNextFreeSlot = () => {
    const nextAvailableRoom = rooms.find((r) => !r.isInCart);
    if (!nextAvailableRoom) return { adults: 0, children: 0, roomNumber: null };

    const rn = nextAvailableRoom.roomNumber;
    const cachedCtx = typeof getSlotPax === "function" ? getSlotPax(rn) : null;
    const cachedRef = stablePaxBySlotRef.current?.[rn];

    const adults =
      nextAvailableRoom.adultsCount ?? cachedCtx?.adults ?? cachedRef?.adults ?? 0;
    const children =
      nextAvailableRoom.childrenCount ?? cachedCtx?.children ?? cachedRef?.children ?? 0;

    return { adults, children, roomNumber: rn };
  };
  const prevDeps = useRef({});
  // ===== carga/recarga de tipos =====
  useEffect(() => {
    const fetchRoomTypes = async () => {
      console.count("fetchRoomTypes ejecutado");
      // ✅ Como fetchRoomTypes puede quedar corriendo más de una vez en paralelo
      // (varias ejecuciones "en cadena" por cambios de rooms/cartItems), todos
      // los console.time/timeEnd de acá abajo usaban el mismo label fijo y se
      // pisaban entre sí ("Timer already exists" / "does not exist" en consola).
      // Esto es solo cosmético para medir tiempos: no cambia ninguna lógica de
      // fetch, solo hace que cada corrida tenga timers propios.
      const dbgId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
const currentDeps = {
  selectedDetails,
  cartItems,
  rooms,
  rutData,
  paxSignature,
  searchTrigger,
  h27BlockedSeason,
  bancoEstadoTipoCliente,
  bancoEstadoLoading,
};

console.group("Dependencias que cambiaron");

Object.entries(currentDeps).forEach(([key, value]) => {
  console.log(
    key,
    prevDeps.current[key] === value ? "IGUAL" : "CAMBIÓ"
  );
});

console.groupEnd();

prevDeps.current = currentDeps;
      console.time(`TOTAL fetchRoomTypes #${dbgId}`);

      if (hotCod === "27" && h27BlockedSeason) {
        setRoomTypeList([]);
        console.log("Entro aca y lo dejo en  null");
        setIsLoading(false);
        setIsHydratingList(false);
        return;
      }

      // Esperar a tener el tipo de cliente para Banco Estado
      if (
        BANCO_ESTADO_HOTELS.includes(String(hotCod)) &&
        !bancoEstadoTipoCliente
      ) {
        return;
      }

      try {
        abortRef.current?.abort();
      } catch (e) { }
      abortRef.current = new AbortController();
      const { signal } = abortRef.current;

      setIsLoading(true);
      setIsHydratingList(true);
      const myFetchId = ++fetchIdRef.current;

      const { adults: totalAdults, children: totalChildren, roomNumber } =
        getStablePaxForNextFreeSlot();

      if (roomNumber == null) {
        setRoomTypeList([]);
        console.log("Entro aca y lo deja en null");
        setIsLoading(false);
        setIsHydratingList(false);
        return;
      }

      const desde = toYmd(selectedDetails.startDate);
      const hasta = toYmd(selectedDetails.endDate);
      console.log("DESDEEEEEEE", desde);
      console.time(`obtenerMuestroTiposHabitacion #${dbgId}`);

      const tipos = await getTiposHabitacionCacheados(hotCod, desde, hasta);
      console.timeEnd(`obtenerMuestroTiposHabitacion #${dbgId}`);
      if (signal.aborted || fetchIdRef.current !== myFetchId) return;

      const worker = async (room) => {
        //agrego esto para medir tiempo
        console.time(`Habitación ${room.TipoHabCodigo} #${dbgId}`);
        if (signal.aborted) return null;

        const ratePlans = await withRetry(async () => {
          let ratePlansLocal = [];
          const preferenceRate =
            rooms.find((r) => !r.isInCart)?.preferenceRate || false;
          if (BANCO_ESTADO_HOTELS.includes(String(hotCod))) {

            if (bancoEstadoTipoCliente) {

              const rate = await obtenerRegimenPorTipoCliente(
                hotCod,
                room.TipoHabCodigo,
                desde,
                hasta,
                bancoEstadoTipoCliente
              );

              setTipoTarifaABuscar("C");
              setCliente(bancoEstadoTipoCliente);

              ratePlansLocal = normalizeToArray(rate?.TarifaTipoHabItem);

            } else {

              ratePlansLocal = [];

            }
          }
          else if (rutData && preferenceRate) {
            if (["6", "7", "8", "9", "10"].includes(hotCod)) {
              const clienteConReserva = await rutTieneReserva(
                datosHotel.MotorHotAdmintour,
                rutData?.datosCliente?.rut,
                desde,
                hasta
              );
              if (clienteConReserva.HayReservas === "N") {
                const segmentoModel = rutData?.segmentoModel || {};
                const tiposClientes = Object.keys(segmentoModel).filter(
                  (k) => segmentoModel[k] === "X"
                );
                const tipoCliente =
                  tiposClientes.find((tc) =>
                    [
                      "empledoLosHeroes",
                      "empleadoPrepago",
                      "empleadoRecreactiva",
                    ].includes(tc)
                  ) ||
                  tiposClientes[0] ||
                  null;

                if (tipoCliente) {
                  const rate = await obtenerRegimenPorTipoCliente(
                    hotCod,
                    room.TipoHabCodigo,
                    desde,
                    hasta,
                    tipoCliente
                  );
                  setTipoTarifaABuscar("C");
                  setCliente(tipoCliente);
                  ratePlansLocal = normalizeToArray(rate?.TarifaTipoHabItem);
                } else {
                  setTipoTarifaABuscar("H");
                  console.log(
                    "codigo hotel, tipo hab, desde, hasta",
                    hotCod,
                    room.TipoHabCodigo,
                    desde,
                    hasta
                  );
                  const rpResp = await obtenerRegimenesTipoHab(
                    hotCod,
                    room.TipoHabCodigo,
                    desde,
                    hasta
                  );
                  ratePlansLocal = Array.isArray(rpResp)
                    ? rpResp
                    : normalizeToArray(rpResp?.TarifaTipoHabItem ?? rpResp);
                }
              } else {
                setTipoTarifaABuscar("H");
                console.log(
                  "codigo hotel, tipo hab, desde, hasta",
                  hotCod,
                  room.TipoHabCodigo,
                  desde,
                  hasta
                );
                const rpResp = await obtenerRegimenesTipoHab(
                  hotCod,
                  room.TipoHabCodigo,
                  desde,
                  hasta
                );
                ratePlansLocal = Array.isArray(rpResp)
                  ? rpResp
                  : normalizeToArray(rpResp?.TarifaTipoHabItem ?? rpResp);
              }
            } else if (hotCod === "21") {
              const tipoCliente = rutData.DatosDocumento[0]?.PaxTipCliente;

              if (tipoCliente) {
                const rate = await obtenerRegimenPorTipoCliente(
                  hotCod,
                  room.TipoHabCodigo,
                  desde,
                  hasta,
                  tipoCliente
                );
                setTipoTarifaABuscar("C");
                setCliente(tipoCliente);
                ratePlansLocal = normalizeToArray(rate?.TarifaTipoHabItem);
              } else {
                setTipoTarifaABuscar("H");
                const rpResp = await obtenerRegimenesTipoHab(
                  hotCod,
                  room.TipoHabCodigo,
                  desde,
                  hasta
                );
                ratePlansLocal = Array.isArray(rpResp)
                  ? rpResp
                  : normalizeToArray(rpResp?.TarifaTipoHabItem ?? rpResp);
              }
            } else if (hotCod === "27") {
              const allowed = ["SBAMAS12", "SBAMENOS12", "SINSBA"];
              const doc0 = Array.isArray(rutData?.DatosDocumento)
                ? rutData.DatosDocumento[0]
                : null;

              const tipoClienteRaw = String(doc0?.PaxTipCliDesc || "")
                .trim()
                .toUpperCase();

              const tipoCliente = allowed.includes(tipoClienteRaw)
                ? tipoClienteRaw
                : null;

              if (tipoCliente) {
                const rate = await obtenerRegimenPorTipoCliente(
                  hotCod,
                  room.TipoHabCodigo,
                  desde,
                  hasta,
                  tipoCliente
                );

                setTipoTarifaABuscar("C");
                setCliente(tipoCliente);
                ratePlansLocal = normalizeToArray(rate?.TarifaTipoHabItem);
              } else {
                setTipoTarifaABuscar("H");
                const rpResp = await obtenerRegimenesTipoHab(
                  hotCod,
                  room.TipoHabCodigo,
                  desde,
                  hasta
                );
                ratePlansLocal = Array.isArray(rpResp)
                  ? rpResp
                  : normalizeToArray(rpResp?.TarifaTipoHabItem ?? rpResp);
              }
            }
          } else {
            setTipoTarifaABuscar("H");
            console.log(
              "codigo hotel, tipo hab, desde, hasta",
              hotCod,
              room.TipoHabCodigo,
              desde,
              hasta
            );
            const rpResp = await obtenerRegimenesTipoHab(
              hotCod,
              room.TipoHabCodigo,
              desde,
              hasta
            );
            console.log("replans uqe retorna la api:", rpResp);
            ratePlansLocal = Array.isArray(rpResp)
              ? rpResp
              : normalizeToArray(rpResp?.TarifaTipoHabItem ?? rpResp);
          }

          return ratePlansLocal;
        });

        const services = await getServiciosDeHabitacionCacheados(
          hotCod,
          room.TipoHabCodigo
        );

        const mappedServices =
          Array.isArray(services) && services.length > 0
            ? services
              .map((service) => {
                const matchedService = roomServices.find(
                  (rs) => rs.service === service.ServTipoHabIdentificador
                );
                return matchedService
                  ? {
                    icon: matchedService.icon,
                    description: service.ServTipoHabNombre,
                  }
                  : null;
              })
              .filter(Boolean)
            : [];

        const adults = totalAdults;
        const children = totalChildren;

        const hotelCodAdm = datosHotel?.MotorHotAdmintour;

        const calcNoLinealTotalLocal = ({ adults, children, nl }) => {
          const t1 = nl?.tarifa1;
          const t2 = nl?.tarifa2;
          const t3 = nl?.tarifa3;
          const t4 = nl?.tarifa4;
          const t5 = nl?.tarifa5;
          const m1 = nl?.menor1;
          const m2 = nl?.menor2;
          const add = nl?.tarifapaxadicional;

          let totalAdult = 0;
          if (adults <= 0) totalAdult = 0;
          else if (adults === 1) totalAdult = t1;
          else if (adults === 2) totalAdult = t2;
          else if (adults === 3) totalAdult = t3;
          else if (adults === 4) totalAdult = t4;
          else if (adults === 5) totalAdult = t5;
          else totalAdult = t5 + (adults - 5) * add;

          let totalChild = 0;
          if (children <= 0) totalChild = 0;
          else if (children === 1) totalChild = m1;
          else if (children === 2) totalChild = m2;
          else totalChild = children * m1;

          return totalAdult + totalChild;
        };

        const ratePlansNorm = normalizeToArray(
          ratePlans?.TarifaTipoHabItem ?? ratePlans
        );

        const ratePlansWithPrices = Array.isArray(ratePlansNorm)
          ? await Promise.all(
            Array.from(
              new Map(ratePlansNorm.map((rp) => [rp.TarifaRegimen, true])).keys()
            ).map(async (regimen) => {
              const sameReg = ratePlansNorm.filter(
                (x) => x.TarifaRegimen === regimen
              );

              const hRp = sameReg.find((x) => x.TarifaAplica === "H");
              const nRp = sameReg.find((x) => x.TarifaAplica === "N");
              const adultRp = sameReg.find((x) => x.TarifaAplica === "A");
              const childRp = sameReg.find((x) => x.TarifaAplica === "M");

              const representative = hRp || nRp || adultRp || sameReg[0];

              if (hRp) {
                const lowestPrice = hRp.TarfaPrecioMinimo;
                return {
                  ratePlan: representative,
                  lowestPrice,
                  ratePlansPorRegimen: sameReg,
                };
              }

              if (representative?.TarifaAplica === "N") {
                const tarifaCodAdm = representative?.TarifaCodAdm;

                if (hotelCodAdm && tarifaCodAdm) {
                  const nl = await withRetry(() =>
                    tarifaNoLineal(hotelCodAdm, tarifaCodAdm, desde)
                  );

                  const lowestPrice = calcNoLinealTotalLocal({
                    adults,
                    children,
                    nl,
                  });

                  return {
                    ratePlan: representative,
                    lowestPrice,
                    ratePlansPorRegimen: sameReg,
                    noLineal: nl,
                  };
                }

                return {
                  ratePlan: representative,
                  lowestPrice: 0,
                  ratePlansPorRegimen: sameReg,
                };
              }

              const adultsMin =
                adultRp?.TarfaPrecioMinimo ??
                adultRp?.TarifaPrecioDesde ??
                0;
              const childrenMin =
                childRp?.TarfaPrecioMinimo ??
                childRp?.TarifaPrecioDesde ??
                0;

              const lowestPrice =
                adultsMin * adults + childrenMin * children;

              return {
                ratePlan: representative,
                lowestPrice,
                ratePlansPorRegimen: sameReg,
              };
            })
          )
          : [];
        console.timeEnd(`Habitación ${room.TipoHabCodigo} #${dbgId}`);
        return {
          roomType: room.TipoHabNombre,
          description1: room.TipoHabTexto1,
          description2: room.TipoHabTexto2,
          description3: room.TipoHabTexto3,
          image: room.TipoHabFotoTxt,
          quantity: room.TipoHabQuedan,
          ratePlans: ratePlansWithPrices,
          services: mappedServices,
          roomTypeCode: room.TarifaCodAdm,
          maxCapacity: room.TipoHabCupoMax,
          minCapacity: room.TipoHabCupoMin,
          habilitadaPorTipoHab: room.TipoHabHabilitoMinimo,
          maximoMinimoDeNoches: room.TipoHabMinimoMaximo,
          detalleTipoHab: room.TipoHabDetalle,
          TipoHabCodigo: room.TipoHabCodigo,
        };
      };
      //agrego para medir tiempo
      console.time(`TOTAL worker #${dbgId}`);

      // Antes: runInBatches(tipos, 3, worker) esperaba a que TERMINARAN los 3
      // tipos de habitación de un lote antes de arrancar el próximo lote, aunque
      // 2 de los 3 ya hubieran terminado (el lote completo iba tan rápido como
      // el más lento). Con el pool de concurrencia, apenas se libera un lugar
      // se toma el siguiente tipo de habitación de la cola.
      const settled = await runWithConcurrency(tipos, 6, worker);

      //agrego para medir tiempo
      console.timeEnd(`TOTAL worker #${dbgId}`);

      if (signal.aborted || fetchIdRef.current !== myFetchId) return;

      const expanded = settled
        .filter((s) => s.status === "fulfilled" && s.value)
        .map((s) => s.value);

      console.log("expanded", expanded);
      const filtered = expanded.filter((rt) => {
        console.log("rtttt", rt);
        let roomQt = 0;
        if (cartItems && Array.isArray(cartItems.cartNoPrice)) {
          const alreadySelected = cartItems.cartNoPrice.filter(
            (item) => item.CarritoItemTipoHab === rt.roomType
          ).length;
          console.log("entra aca");
          roomQt = rt.quantity - alreadySelected;
          console.log("roomQt", roomQt);
        } else {
          roomQt = rt.quantity;
        }

        const totalPeople = totalAdults + totalChildren;
        rt.quantity = roomQt;
        console.log("ROOMQT", roomQt);
        console.log("RT MAX CAPACITY", rt.maxCapacity);
        console.log("total people", totalPeople);
        console.log("rt.maxCapacity >= totalPeople", rt.maxCapacity >= totalPeople);
        console.log("RT min CAPACITY", rt.minCapacity);
        console.log("total people", totalPeople);
        console.log("rt.minCapacity >= totalPeople", rt.minCapacity >= totalPeople);
        console.log("a ver si es un array", Array.isArray(rt.ratePlans));
        console.log("rt.ratePlans.length", rt);

        return (
          roomQt > 0 &&
          rt.maxCapacity >= totalPeople &&
          rt.minCapacity <= totalPeople &&
          Array.isArray(rt.ratePlans) &&
          rt.ratePlans.length > 0
        );
      });
      console.log("rooms filtrados", filtered);
      setRoomTypeList(filtered);
      setListVersion((v) => v + 1);
      setIsLoading(false);
      setIsHydratingList(false);
      console.timeEnd(`TOTAL fetchRoomTypes #${dbgId}`);
      if (filtered.length === 0) {
        resetNearSearch();
        setAltFinished(false);
        setAltHotels([]);

        await Promise.all([
          (async () => {
            setNearFinished(false);
            await fetchMoreNearPeriods({ desde, hasta, initial: true });
          })(),
          (async () => {
            await findAlternativeHotels({ desde, hasta });
          })(),
        ]);
      } else {
        setAltHotels([]);
        setAltFinished(false);
        resetNearSearch();
      }
    };

    fetchRoomTypes();

    return () => {
      try {
        abortRef.current?.abort();
      } catch (e) { }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, 
  [
    selectedDetails,
    cartItems,
    rooms,
    rutData,
    paxSignature,
    searchTrigger,
    h27BlockedSeason,
    bancoEstadoTipoCliente,
    bancoEstadoLoading,
  ]
    );

  // ===== modales =====
  const handleClickOpen = (room) => {
    setSelectedRoom(room);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleRatePlanClickOpen = (ratePlan) => {
    const { adults: totalAdults, children: totalChildren } =
      getStablePaxForNextFreeSlot();

    setSelectedRatePlan({
      ratePlan,
      room: selectedRoom,
      totalAdults,
      totalChildren,
    });
    setRatePlanDialogOpen(true);
  };

  const handleRatePlanClose = () => {
    setRatePlanDialogOpen(false);
  };

  // ===== borrar del carrito cuando cambian PAX =====
  const removeGroupFromCart = async (groupId) => {
    if (!groupId) return;
    const current = (await obtenerMuestroCarrito(hotCod, sesion)) || [];
    const baseItem = current.find((i) => i.CarritoItemLinea === Number(groupId));
    if (!baseItem) return;

    const linesToDelete = current
      .filter(
        (i) =>
          sameStay(i, baseItem) &&
          (i.CarritoItemLinea === Number(groupId) ||
            i.habitacioncarrito === Number(groupId))
      )
      .sort((a, b) => {
        const aIsBase = a.CarritoItemLinea === Number(groupId);
        const bIsBase = b.CarritoItemLinea === Number(groupId);
        if (aIsBase && !bIsBase) return 1;
        if (!aIsBase && bIsBase) return -1;
        return 0;
      });

    for (const it of linesToDelete) {
      try {
        await eliminarItemCarrito(hotCod, sesion, it.CarritoItemLinea);
      } catch (err) {
        console.error("Error eliminando línea", it.CarritoItemLinea, err);
      }
    }

    const updatedCart = await obtenerMuestroCarrito(hotCod, sesion);
    console.log("=== CARRITO GRABADO ===");
    console.log(updatedCart);
    const newTotal = (updatedCart || []).reduce(
      (s, it) => s + parseFloat(it.CarritoItemTotal || 0),
      0
    );
    setCartItems({ cartNoPrice: updatedCart || [], price: newTotal });
  };

  useEffect(() => {
    if (!paxChangeSignal?.seq || !Array.isArray(paxChangeSignal.changedSlots))
      return;

    const run = async () => {
      for (const slot of paxChangeSignal.changedSlots) {
        const entry = Object.entries(groupSlotMap || {}).find(
          ([, rn]) => Number(rn) === Number(slot)
        );
        const groupId = entry ? Number(entry[0]) : null;

        const roomNow = (rooms || []).find(
          (r) => Number(r.roomNumber) === Number(slot)
        );
        if (roomNow && typeof setSlotPax === "function") {
          setSlotPax(Number(slot), {
            adults: roomNow.adultsCount ?? 0,
            children: roomNow.childrenCount ?? 0,
          });
        }

        if (groupId) {
          try {
            await removeGroupFromCart(groupId);
          } catch (e) {
            console.error(e);
          }
          if (typeof unbindGroup === "function") unbindGroup(groupId);
          updateRoom(Number(slot), false);

          setSnack({
            open: true,
            severity: "warning",
            message: `Cambiaste los pasajeros de la habitación ${slot}. Se eliminó del carrito. Volvé a seleccionarla con la nueva cantidad.`,
          });
        }
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paxChangeSignal?.seq]);

  // ===== selección =====
  const handleSelect = async (roomType, ratePlan, versionAtClick) => {
    if (hotCod === "27" && h27BlockedSeason) {
      setSnack({
        open: true,
        severity: "warning",
        message:
          "Dicho cliente ya cuenta con reserva para el periodo de la temporada, por lo tanto no podra realizar otra reserva",
        persist: true,
      });
      return;
    }

    if (versionAtClick !== listVersion || isHydratingList) return;

    setIsSelecting(true);

    try {
      const cart = await obtenerMuestroCarrito(hotCod, sesion);
      const selectedRoomsCount = new Set((cart || []).map((i) => getGroupId(i)))
        .size;
      if (selectedRoomsCount >= rooms.length) return;

      const nextAvailableRoom = rooms.find((r) => !r.isInCart);
      const selectedRoomNumber = nextAvailableRoom
        ? nextAvailableRoom.roomNumber
        : null;

      const paxCache =
        typeof getSlotPax === "function" && selectedRoomNumber != null
          ? getSlotPax(selectedRoomNumber)
          : null;

      const totalAdults = nextAvailableRoom?.adultsCount ?? paxCache?.adults ?? 0;
      const totalChildren =
        nextAvailableRoom?.childrenCount ?? paxCache?.children ?? 0;

      let groupIdCreated = null;

      const isNoLinealForSelection =
        Array.isArray(ratePlan?.ratePlansPorRegimen) &&
        ratePlan.ratePlansPorRegimen.some((rp) => rp?.TarifaAplica === "N");

      console.log("ME DICE SI ES NO LINEALL", isNoLinealForSelection);

      const hasAMForSelection =
        !isNoLinealForSelection &&
        Array.isArray(ratePlan?.ratePlansPorRegimen) &&
        ratePlan.ratePlansPorRegimen.some(
          (rp) => rp?.TarifaAplica === "A" || rp?.TarifaAplica === "M"
        );

      if (isNoLinealForSelection) {
        console.log("ENTRO ACA A LO DE NO LINEAK");
        const nRatePlan = ratePlan.ratePlansPorRegimen.find(
          (rp) => rp?.TarifaAplica === "N"
        );
        console.log("rateplanssssss", nRatePlan);
        const carritoN = {
          hotelcarrito: hotCod,
          Sesion: sesion,
          DesdeFecha: selectedDetails.startDate,
          hastafecha: selectedDetails.endDate,
          mayores: totalAdults,
          menores: totalChildren,
          TipoHab: nRatePlan?.TarifaTipoHab,
          tarifa: parseInt(
            nRatePlan?.TarifaCodAdm ?? ratePlan?.ratePlan?.TarifaCodAdm,
            10
          ),
        };
        console.log("=== CARRITO PREVIO AL GRABADO ===")
        console.log(carritoN);
        const resp = await grabarCarrito(carritoN);
        groupIdCreated = resp?.UltimaLinea ?? null;

        const updatedCart = await obtenerMuestroCarrito(hotCod, sesion);
        console.log("=== CARRITO GRABADO ===");
        console.log(updatedCart);
        const newTotalPrice = (updatedCart || []).reduce(
          (sum, it) => sum + parseFloat(it?.CarritoItemTotal || 0),
          0
        );

        setCartItems({ cartNoPrice: updatedCart, price: newTotalPrice });
      } else if (hasAMForSelection) {
        const adultRatePlan = ratePlan.ratePlansPorRegimen.find(
          (rp) => rp.TarifaAplica === "A"
        );
        const childRatePlan = ratePlan.ratePlansPorRegimen.find(
          (rp) => rp.TarifaAplica === "M"
        );

        if (totalAdults !== 0 && adultRatePlan) {
          const carritoA = {
            hotelcarrito: hotCod,
            Sesion: sesion,
            DesdeFecha: selectedDetails.startDate,
            hastafecha: selectedDetails.endDate,
            mayores: totalAdults,
            menores: totalChildren,
            TipoHab: adultRatePlan.TarifaTipoHab,
            tarifa: parseInt(adultRatePlan.TarifaCodAdm, 10),
          };

          const grabA = await grabarCarrito(carritoA);
          groupIdCreated = grabA?.UltimaLinea ?? null;
        }

        if (totalChildren !== 0 && childRatePlan && groupIdCreated) {
          const carritoC = {
            hotelcarrito: hotCod,
            Sesion: sesion,
            habitacionCarrito: groupIdCreated,
            DesdeFecha: selectedDetails.startDate,
            hastafecha: selectedDetails.endDate,
            mayores: totalAdults,
            menores: totalChildren,
            TipoHab: childRatePlan.TarifaTipoHab,
            tarifa: parseInt(childRatePlan.TarifaCodAdm, 10),
          };

          console.log("=== CARRITO PREVIO AL GRABADO ===")
          console.log(carritoC);

          await grabarCarrito(carritoC);
        }

        const updatedCart = await obtenerMuestroCarrito(hotCod, sesion);
        console.log("=== CARRITO GRABADO ===");
        console.log(updatedCart);
        setCartItems({ cartNoPrice: updatedCart, price: 0 });
      } else {
        const carrito = {
          hotelcarrito: hotCod,
          Sesion: sesion,
          DesdeFecha: selectedDetails.startDate,
          hastafecha: selectedDetails.endDate,
          mayores: totalAdults,
          menores: totalChildren,
          TipoHab: ratePlan?.ratePlan?.TarifaTipoHab,
          tarifa: parseInt(ratePlan?.ratePlan?.TarifaCodAdm, 10),
        };
        console.log("=== CARRITO PREVIO AL GRABADO ===")
        console.log(carrito);
        const resp = await grabarCarrito(carrito);
        groupIdCreated = resp?.UltimaLinea ?? null;

        const updatedCart = await obtenerMuestroCarrito(hotCod, sesion);
        console.log("=== CARRITO GRABADO ===");
        console.log(updatedCart);
        setCartItems({ cartNoPrice: updatedCart, price: 0 });
      }

      if (groupIdCreated && selectedRoomNumber != null) {
        if (typeof bindGroupToSlot === "function") {
          bindGroupToSlot(groupIdCreated, selectedRoomNumber);
        }
        if (typeof setSlotPax === "function") {
          setSlotPax(selectedRoomNumber, {
            adults: totalAdults,
            children: totalChildren,
          });
        }
      }

      if (selectedRoomNumber != null) updateRoom(selectedRoomNumber, true);
    } catch (error) {
      console.error("Error en handleSelect:", error);
    } finally {
      setIsSelecting(false);
    }
  };

  // ===== render =====
  return (
    <div>
      <Collapse in={isHydratingList || isSelecting}>
        <Box sx={{ position: "sticky", top: 0, zIndex: (t) => t.zIndex.appBar }}>
          <LinearProgress />
        </Box>
      </Collapse>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      {rooms.every((room) => room.isInCart) ? (
        <Typography
          variant="h6"
          component="div"
          color="text.secondary"
          sx={{ textAlign: "center", mt: 4 }}
        >
          Todas las habitaciones necesarias han sido seleccionadas.
        </Typography>
      ) : isHydratingList && roomTypeList.length === 0 ? (
        <>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography variant="h6" component="div" color="text.primary">
              Seleccionando la habitación...
            </Typography>
          </Box>
          {[1, 2, 3].map((k) => (
            <Card
              key={k}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                mb: 4,
              }}
            >
              <Skeleton
                variant="rectangular"
                sx={{ width: { xs: "100%", md: 392 }, height: 200 }}
              />
              <Box sx={{ flex: "1 0 auto", p: 2 }}>
                <Skeleton variant="text" height={36} />
                <Skeleton variant="text" />
                <Skeleton variant="text" width="60%" />
                <Skeleton
                  variant="rectangular"
                  height={32}
                  width={120}
                  sx={{ mt: 2 }}
                />
              </Box>
            </Card>
          ))}
        </>
      ) : roomTypeList.length === 0 ? (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h6"
            component="div"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            No hay disponibilidad para este hotel en el período seleccionado.
          </Typography>

          <Box sx={{ mt: 2.5, maxWidth: 1100, mx: "auto" }}>
            {nearLoading && nearPeriods.length === 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ textAlign: "center", fontWeight: 800 }}
                >
                  Buscando disponibilidad en fechas cercanas…
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", mb: 1.5 }}
                >
                  Te mostramos períodos cercanos con la misma cantidad de noches.
                </Typography>
                <Box sx={{ px: { xs: 1, md: 6 } }}>
                  <LinearProgress />
                </Box>
              </Box>
            )}

            {nearPeriods.length > 0 && (
              <Box sx={{ mb: 1, textAlign: "center" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Fechas cercanas disponibles
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Deslizá para ver más opciones.
                </Typography>
              </Box>
            )}

            {nearFinished && !nearLoading && nearPeriods.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mt: 1.5 }}
              >
                No encontramos disponibilidad en fechas cercanas (±14 días).
              </Typography>
            )}

            {nearPeriods.length > 0 && (
              <Box
                ref={nearScrollRef}
                onScroll={handleNearScroll}
                sx={{
                  display: "flex",
                  gap: 1.2,
                  overflowX: "auto",
                  py: 1,
                  px: 0.5,
                  scrollSnapType: "x mandatory",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": { height: 8 },
                }}
              >
                {nearPeriods.map((p) => (
                  <Card
                    key={`${p.desde}-${p.hasta}`}
                    sx={{
                      minWidth: 200,
                      maxWidth: 220,
                      flex: "0 0 auto",
                      borderRadius: 3,
                      scrollSnapAlign: "start",
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box
                        sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 900, lineHeight: 1.2 }}
                        >
                          {fmtRange(p.desde, p.hasta)}
                        </Typography>
                        <Chip
                          label={p.offsetDays > 0 ? `+${p.offsetDays}` : `${p.offsetDays}`}
                          size="small"
                        />
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.75, mb: 1 }}
                      >
                        {p.fromQty ? `Aprox. ${p.fromQty} habs` : "Con stock"}
                      </Typography>

                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={() => redirectToSameHotelWithDates(p.desde, p.hasta)}
                        sx={{ borderRadius: 999 }}
                      >
                        Ver
                      </Button>
                    </CardContent>
                  </Card>
                ))}

                {nearLoading && nearPeriods.length > 0 && (
                  <Card sx={{ minWidth: 200, flex: "0 0 auto", borderRadius: 3 }}>
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                        Buscando…
                      </Typography>
                      <LinearProgress />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1 }}
                      >
                        Cargando más fechas
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {nearFinished && !nearLoading && nearHasMore && (
                  <Box
                    sx={{
                      minWidth: 200,
                      flex: "0 0 auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 1.5,
                      color: "text.secondary",
                      fontSize: 13,
                    }}
                  >
                    Seguí deslizando →
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {HERO_GROUP.includes(String(hotCod)) && (
            <Box sx={{ mt: 3, maxWidth: 1100, mx: "auto" }}>
              {altLoading && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ textAlign: "center", fontWeight: 700, mb: 1 }}
                  >
                    Buscando parques alternativos para tus mismas fechas…
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", mb: 1.5 }}
                  >
                    Estamos consultando disponibilidad en los demás parques.
                  </Typography>

                  <Box sx={{ px: { xs: 1, md: 6 } }}>
                    <LinearProgress />
                  </Box>
                </Box>
              )}

              {!altLoading && altHotels.length > 0 && (
                <Box sx={{ mb: 1.5, textAlign: "center" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Estos son los parques que tienen disponibilidad para la fecha que
                    estás buscando
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Elegí uno para continuar tu búsqueda.
                  </Typography>
                </Box>
              )}

              {altFinished && !altLoading && altHotels.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 2 }}
                >
                  No encontramos disponibilidad en los parques alternativos para el
                  mismo período.
                </Typography>
              )}

              {altLoading ? (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {[1, 2].map((k) => (
                    <Grid item xs={12} md={6} key={k}>
                      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                        <Skeleton variant="rectangular" height={170} />
                        <Box sx={{ p: 2 }}>
                          <Skeleton height={30} width="70%" />
                          <Skeleton height={20} width="55%" />
                          <Skeleton height={20} width="80%" />
                          <Skeleton height={42} width="45%" sx={{ mt: 1.5 }} />
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : altHotels.length > 0 ? (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {altHotels.map((h) => (
                    <Grid item xs={12} md={6} key={h.hotcod}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          overflow: "hidden",
                          transition: "transform .15s ease, box-shadow .15s ease",
                          "&:hover": { transform: "translateY(-2px)" },
                        }}
                      >
                        <Box sx={{ position: "relative" }}>
                          {h.banner ? (
                            <CardMedia
                              component="img"
                              height="170"
                              image={h.banner}
                              alt={h.name}
                            />
                          ) : (
                            <Box
                              sx={{
                                height: 170,
                                background:
                                  "linear-gradient(135deg, rgba(25,118,210,0.20), rgba(156,39,176,0.14))",
                              }}
                            />
                          )}

                          <Box sx={{ position: "absolute", top: 12, left: 12 }}>
                            <Chip label="Disponible para tus fechas" size="small" />
                          </Box>
                        </Box>

                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 0.5 }}>
                            {h.name}
                          </Typography>

                          <Divider sx={{ my: 1.5 }} />

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {h.fromQty
                              ? `Disponibilidad encontrada (aprox. ${h.fromQty} habs)`
                              : "Disponibilidad encontrada"}
                          </Typography>

                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}
                          >
                            {(h.roomTypes || []).slice(0, 4).map((rt) => (
                              <Chip key={rt} label={rt} size="small" />
                            ))}
                          </Box>

                          <Button
                            fullWidth
                            variant="contained"
                            onClick={() => redirectToHotel(h.hotcod, h.name)}
                            sx={{ borderRadius: 999 }}
                          >
                            Ver disponibilidad
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : null}
            </Box>
          )}
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            {(() => {
              const next = rooms.find((room) => !room.isInCart);
              const nextRoomNumber = next ? next.roomNumber : rooms.length + 1;
              return (
                <Typography variant="h6" component="div" color="text.primary">
                  Seleccionando la habitación {nextRoomNumber}
                </Typography>
              );
            })()}
          </Box>

          {roomTypeList.map((roomType, index) => (
            <Box
              key={index}
              sx={{
                mb: 4,
                opacity: roomType.habilitadaPorTipoHab === "N" ? 0.5 : 1,
                pointerEvents:
                  roomType.habilitadaPorTipoHab === "N" ? "none" : "auto",
              }}
              className="boxRoomType"
            >
              <Card
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                }}
                className="boxRoomType"
              >
                <CardMedia
                  component="img"
                  sx={{ width: { xs: "100%", md: 392 }, height: 200 }}
                  image={`data:image/png;base64,${roomType.image}`}
                  alt="Room Name"
                />
                <CardContent sx={{ flex: "1 0 auto", spacing: 3 }} className="boxRoomType">
                  <Typography variant="h5" component="div">
                    {roomType.roomType}
                  </Typography>

                  <Typography variant="subtitle1" color="text.secondary" component="div">
                    {roomType.description1}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" component="div">
                    {roomType.description2}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" component="div">
                    {roomType.description3}
                  </Typography>

                  <Typography variant="subtitle1" color="text.secondary" component="div">
                    {Array.isArray(roomType.services) &&
                      roomType.services.length > 0 &&
                      roomType.services.map((service, idx) =>
                        service ? (
                          <span key={idx} style={{ fontSize: "20px", marginRight: "8px" }}>
                            {service.icon}
                          </span>
                        ) : null
                      )}
                  </Typography>

                  <Link
                    href="#"
                    color="#007289"
                    onClick={() => {
                      setSelectedRoom(roomType);
                      setDialogOpen(true);
                    }}
                    sx={{
                      pointerEvents:
                        roomType.habilitadaPorTipoHab === "N" ? "none" : "auto",
                    }}
                  >
                    Ver más
                  </Link>
                </CardContent>
              </Card>

              {roomType.habilitadaPorTipoHab === "N" ? (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mt: 1, textAlign: "center", fontStyle: "italic" }}
                >
                  Esta habitación tiene una restricción de mínimo{" "}
                  {roomType.maximoMinimoDeNoches} noches para el período seleccionado.
                </Typography>
              ) : (
                <div>
                  {roomType.ratePlans &&
                    [
                      ...new Map(
                        roomType.ratePlans.map((item) => [
                          item.ratePlan.TarifaRegimen,
                          item,
                        ])
                      ).values(),
                    ].map((ratePlan, rateIndex) => {
                      const nightsBetween = (startDate, endDate) => {
                        if (!startDate || !endDate) return 0;

                        const s = new Date(
                          Date.UTC(
                            startDate.getFullYear(),
                            startDate.getMonth(),
                            startDate.getDate()
                          )
                        );

                        const e = new Date(
                          Date.UTC(
                            endDate.getFullYear(),
                            endDate.getMonth(),
                            endDate.getDate()
                          )
                        );

                        return Math.round((e - s) / (1000 * 60 * 60 * 24));
                      };

                      const nochesSeleccionadas = nightsBetween(
                        selectedDetails.startDate,
                        selectedDetails.endDate
                      );

                      console.log("[MINNOCHES CHECK]", {
                        desc: ratePlan?.ratePlan?.TarifaDescripcion,
                        tarifaUsaMinimo: ratePlan?.ratePlan?.TarifaUsaMinimo,
                        tipoTarifaUsaMinimo: typeof ratePlan?.ratePlan?.TarifaUsaMinimo,
                        minimoNoches: ratePlan?.ratePlan?.TarifaMinimoNoches,
                        tipoMinimoNoches: typeof ratePlan?.ratePlan?.TarifaMinimoNoches,
                        start: selectedDetails?.startDate,
                        end: selectedDetails?.endDate,
                        nochesSeleccionadas,
                      });

                      const tarifaUsaMinimo = Number(
                        ratePlan?.ratePlan?.TarifaUsaMinimo || 0
                      );
                      const minimoNoches = Number(
                        ratePlan?.ratePlan?.TarifaMinimoNoches || 0
                      );

                      const tarifaRestringida =
                        (tarifaUsaMinimo === 1 || tarifaUsaMinimo === 2) &&
                        minimoNoches > nochesSeleccionadas;

                      return (
                        <Card
                          key={rateIndex}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: { xs: "column", md: "row" },
                          }}
                          className="cardRate"
                        >
                          <CardContent sx={{ flex: "1 0 auto", spacing: 3 }} className="cardRate">
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                component="div"
                              >
                                {ratePlan.ratePlan.TarifaDescripcion}
                              </Typography>
                              {roomType.quantity < 5 && (
                                <Chip
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  label={`Solo queda${roomType.quantity === 1 ? "" : "n"
                                    } ${roomType.quantity}`}
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Box>
                            <Link
                              href="#"
                              variant="body2"
                              color="#007289"
                              onClick={() => handleRatePlanClickOpen(ratePlan)}
                            >
                              Detalle de la tarifa
                            </Link>
                          </CardContent>

                          <CardContent
                            sx={{
                              flex: "1 0 auto",
                              spacing: 3,
                              textAlign: { xs: "left", md: "right" },
                            }}
                            className="cardRate"
                          >
                            <Typography
                              variant="subtitle1"
                              color="text.secondary"
                              component="div"
                            >
                              {(() => {
                                const moneda = ratePlan?.ratePlan?.TarifaMonDsc || "";
                                const precioReal =
                                  parseFloat(ratePlan?.lowestPrice ?? 0) || 0;
                                const precioRack =
                                  parseFloat(
                                    ratePlan?.ratePlan?.TarifaPrecioRack ?? 0
                                  ) || 0;
                                const ahorro = precioRack - precioReal;

                                const fmt = (n) =>
                                  (parseFloat(n) || 0).toLocaleString("es-ES", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  });

                                const mostrarRack = precioRack > 0;
                                const mostrarAhorro = mostrarRack && ahorro > 0;

                                return (
                                  <>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "baseline",
                                        gap: 6,
                                        flexWrap: "wrap",
                                        justifyContent: "flex-end",
                                        width: "100%",
                                      }}
                                    >
                                      <span style={{ opacity: 0.9 }}>desde</span>

                                      <span
                                        style={{
                                          fontWeight: 900,
                                          fontSize: 18,
                                          opacity: 0.95,
                                        }}
                                      >
                                        {moneda} {fmt(precioReal)}
                                      </span>

                                      <span style={{ opacity: 0.9 }}>/ noche</span>
                                    </span>

                                    {mostrarAhorro && (
                                      <div
                                        style={{
                                          marginTop: 6,
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          width: "100%",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 13,
                                            fontWeight: 800,
                                            color: "#F57C00",
                                          }}
                                        >
                                          Ahorras desde{" "}
                                          <span style={{ whiteSpace: "nowrap" }}>
                                            {moneda} {fmt(ahorro)}
                                          </span>{" "}
                                          / noche
                                        </span>
                                      </div>
                                    )}

                                    {mostrarRack && (
                                      <div
                                        style={{
                                          marginTop: 6,
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          width: "100%",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 12,
                                            opacity: 0.8,
                                            padding: "2px 8px",
                                            borderRadius: 8,
                                            background: "rgba(0,0,0,0.04)",
                                            maxWidth: "100%",
                                          }}
                                        >
                                          <span style={{ marginRight: 6 }}>
                                            Tarifa No Afiliado
                                          </span>

                                          <span style={{ whiteSpace: "nowrap" }}>
                                            {moneda} {fmt(precioRack)}
                                          </span>
                                          <span style={{ marginRight: 6 }}>
                                            {" "}
                                            / noche
                                          </span>
                                        </span>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" component="div">
                              por habitación
                            </Typography>

                            <Button
                              variant="contained"
                              disabled={tarifaRestringida || isSelecting || isHydratingList}
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!tarifaRestringida) {
                                  await handleSelect(roomType, ratePlan, listVersion);
                                }
                              }}
                            >
                              Seleccionar
                            </Button>

                            {tarifaRestringida && (
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ mt: 1, fontStyle: "italic" }}
                              >
                                Esta tarifa tiene un mínimo de {minimoNoches} noches.
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              )}
            </Box>
          ))}
        </>
      )}

      <DialogSeeMoreRoomType
        open={dialogOpen}
        handleClose={handleClose}
        room={selectedRoom}
      />
      <RatePlanDetail
        open={ratePlanDialogOpen}
        handleClose={handleRatePlanClose}
        ratePlan={selectedRatePlan}
        selectedRange={selectedDetails}
        room={selectedRoom}
        totalAdults={selectedRatePlan?.totalAdults}
        totalChildren={selectedRatePlan?.totalChildren}
        tipoTarifaABuscar={tipoTarifaABuscar}
        cliente={cliente}
      />
    </div>
  );
}

export default RoomTypeRatePlan;